/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Fragment, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { TextField } from "@mui/material";
import { SettingsFormatEdit } from "cms/fields/layout/controls/settings-format";
import { SettingsStyleEdit } from "cms/fields/layout/controls/settings-style";
import { SettingsTargetEdit } from "cms/fields/layout/controls/settings-targets";
import { useSelectFieldSchema } from "cms/fields/hooks/use-select-field-schema";
import { DisplaySettingsWrapper } from "cms/fields/layout/display-settings-wrapper";
import { ListItemContext } from "cms/fields/field-lists/field-list-context";
// import useThrottle from 'hooks/useThrottle'

const UrlFieldItemEdit = () => {
    
    const { t } = useTranslation();
    // const throttler = useThrottle()
    
    const ctx = useContext(ListItemContext);
    const { type, name, index, value, settings, updateListItem, disabled = false } = ctx;

    const schema = useSelectFieldSchema(name);
    const { settings: schemaSettings } = schema || {};
    const { options: schemaOptions, styleOptions, formatOptions, placeholder } = schemaSettings || {};
    
    const ph = placeholder || t("commons.placeholderText");
    const initialState = {
        value: value ? value : "",
        settings
    };
    const [itemLocalState, setItemLocalState] = useState(initialState);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const update = useCallback(
        debounce((updateState, index) => {
            updateListItem(updateState, index);
        }, 1200),
        [updateListItem]
    );
    
    const onSettingsChange = (updateState) => {
        setItemLocalState(updateState);
        update(updateState, index);
    };
    
    const onChange = (event) => {
        const newValue = event.target.value;
        const newState = { ...itemLocalState, value: newValue };
        setItemLocalState(newState);
        update(newState, index);
    };

    return (
        <Fragment>
            <div className='simplicity-composer-field-primary-layout'>
                <div className='simplicity-composer-field-primary'>
                    <TextField
                        fullWidth={true}
                        placeholder={ph}
                        value={itemLocalState?.value}
                        disabled={disabled}
                        variant='standard'
                        onChange={onChange}
                    />
                </div>
                <DisplaySettingsWrapper>
                    {/*  Reads from schema options.styleOptions */}
                    {/*  records selection as value settings.style */}
                    <SettingsStyleEdit
                        type={type}
                        name={name}
                        label={t("commons.displayStyle")}
                        itemLocalState={itemLocalState}
                        setItemLocalState={setItemLocalState}
                        disabled={disabled}
                        styleOptions={styleOptions}
                        selected={itemLocalState?.settings?.style}
                        index={index}
                        update={onSettingsChange}
                    />
                    {/*  Reads from schema options.formatOptions */}
                    {/*  records selection as value settings.format */}
                    <SettingsFormatEdit
                        type={type}
                        name={name}
                        label={t("commons.displayAs")}
                        itemLocalState={itemLocalState}
                        setItemLocalState={setItemLocalState}
                        disabled={disabled}
                        formatOptions={formatOptions}
                        selected={itemLocalState?.settings?.format}
                        index={index}
                        update={onSettingsChange}
                    />
                    {/*  Reads from schema options.targets */}
                    {/*  records selection as value settings.options.openIn */}
                    <SettingsTargetEdit
                        type={type}
                        name={name}
                        label={t("commons.openIn")}
                        itemLocalState={itemLocalState}
                        setItemLocalState={setItemLocalState}
                        disabled={disabled}
                        schemaOptions={schemaOptions}
                        selected={itemLocalState?.settings?.options?.openIn}
                        index={index}
                        update={onSettingsChange}
                    />
                </DisplaySettingsWrapper>
            </div>
        </Fragment>
    );
};
export { UrlFieldItemEdit };
